import React, { useState, lazy, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShieldCheck, Award, FileCheck, Download, Share2, Check, ZoomIn } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
const certs = [{
  id: 1,
  title: 'Special Control Unit Against Money Laundering',
  abbr: 'SCUML',
  refNo: 'RN: SC 088115',
  issued: '6 April 2021',
  desc: 'Certified compliance with anti-money laundering regulations and financial transparency standards under Section 5 (1) (a) and (4) of the Money Laundering (Prohibition) Act, 2011.',
  image: 'https://cdn.magicpatterns.com/uploads/9En4RJF3c5FfqsNJU2ZXqc/scuml.png',
  icon: ShieldCheck
}, {
  id: 2,
  title: 'Lagos State Real Estate Regulatory Authority',
  abbr: 'LASRERA',
  refNo: 'BOS/100040R/2024',
  issued: '17 October 2024',
  desc: 'Permit No: BOS/100040R/2024. Fully licensed to operate as a recognized property developer in Lagos State, valid through 17 September 2025.',
  image: 'https://cdn.magicpatterns.com/uploads/q1HaJzEyubU6NTpUi2cAea/pasted-image.jpg',
  icon: Award
}, {
  id: 3,
  title: 'Corporate Affairs Commission',
  abbr: 'CAC',
  refNo: 'RC 1668319',
  issued: '16 March 2020',
  desc: 'Certificate of Incorporation. Legally registered and recognized entity in the Federal Republic of Nigeria, incorporated under the Companies and Allied Matters Act 1990.',
  image: 'https://cdn.magicpatterns.com/uploads/g2rww3fNFnZfXFUxfpERh6/pasted-image.jpg',
  icon: FileCheck
}];
export function Certifications() {
  const [selectedCert, setSelectedCert] = useState<(typeof certs)[0] | null>(null);
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const handleSave = (cert: (typeof certs)[0]) => {
    // Trigger download by opening image in new tab via anchor with download attr
    const a = document.createElement('a');
    a.href = cert.image;
    a.download = `Zylus-${cert.abbr}-Certificate.${cert.image.split('.').pop()?.split('?')[0] || 'jpg'}`;
    a.target = '_blank';
    a.rel = 'noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };
  const handleShare = async (cert: (typeof certs)[0]) => {
    const shareData = {
      title: `Zylus Homes — ${cert.abbr} Certificate`,
      text: `${cert.title} (${cert.refNo}) — issued to Zylus Homes & Property Ltd.`,
      url: cert.image
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
        return;
      } catch {
        /* fall through to copy */
      }
    }
    try {
      await navigator.clipboard.writeText(shareData.url);
      setCopiedId(cert.id);
      setTimeout(() => setCopiedId(null), 1800);
    } catch {
      window.open(cert.image, '_blank');
    }
  };
  return <div className="pt-28 pb-24" data-id="element-997">
      <PageHeader eyebrow="Compliance" title="Certifications and License" subtitle="Here are all certifications and licenses for Zylus Homes. We operate with full transparency and regulatory compliance." breadcrumb={[{
      label: 'Home',
      href: '/'
    }, {
      label: 'About Us'
    }, {
      label: 'Certifications'
    }]} data-id="element-998" />

      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 md:px-12 mt-16" data-id="element-999">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-id="element-1000">
          {certs.map((cert, i) => {
          const Icon = cert.icon;
          return <motion.article key={cert.id} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1,
            duration: 0.6
          }} className="bg-white rounded-3xl overflow-hidden border border-ink/5 shadow-sm hover:shadow-xl transition-all duration-500 flex flex-col group" data-id="element-1001">
                {/* Certificate preview — full image, properly centered */}
                <div className="relative bg-gradient-to-br from-bone to-ink/5 p-4 sm:p-6 flex items-center justify-center aspect-[4/3] overflow-hidden" data-id="element-1002">
                  <motion.img whileHover={{
                scale: 1.04
              }} transition={{
                duration: 0.5
              }} src={cert.image} alt={cert.title} className="max-w-full max-h-full w-auto h-auto object-contain rounded-lg shadow-md" loading="lazy" data-id="element-1003" />

                  {/* Zoom hint */}
                  <button onClick={() => setSelectedCert(cert)} aria-label={`Open ${cert.abbr} certificate`} className="absolute inset-0 flex items-center justify-center bg-ink/0 hover:bg-ink/20 transition-colors duration-300 opacity-0 group-hover:opacity-100" data-id="element-1004">
                    <span className="bg-white/95 text-ink rounded-full px-4 py-2 text-xs font-bold uppercase tracking-widest flex items-center gap-1.5 shadow-lg" data-id="element-1005">
                      <ZoomIn className="w-3.5 h-3.5" data-id="element-1006" /> View Full
                    </span>
                  </button>

                  {/* Floating badge */}
                  <div className="absolute top-3 right-3 w-10 h-10 sm:w-12 sm:h-12 bg-white rounded-full shadow-lg flex items-center justify-center" data-id="element-1007">
                    <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-gold" data-id="element-1008" />
                  </div>
                </div>

                <div className="p-6 sm:p-8 flex flex-col flex-grow" data-id="element-1009">
                  <div className="text-[10px] font-bold text-gold-dark uppercase tracking-[0.25em] mb-2" data-id="element-1010">
                    {cert.abbr}
                  </div>
                  <h3 className="text-lg sm:text-xl font-serif text-ink mb-2 leading-snug" data-id="element-1011">
                    {cert.title}
                  </h3>
                  <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-ink/50 mb-4" data-id="element-1012">
                    <span className="font-mono" data-id="element-1013">{cert.refNo}</span>
                    <span data-id="element-1014">· Issued {cert.issued}</span>
                  </div>
                  <p className="text-ink/60 text-sm mb-6 flex-grow leading-relaxed" data-id="element-1015">
                    {cert.desc}
                  </p>

                  {/* Actions */}
                  <div className="flex flex-col sm:flex-row gap-2" data-id="element-1016">
                    <button onClick={() => setSelectedCert(cert)} className="flex-1 bg-gold hover:bg-gold-dark text-ink font-bold text-[11px] uppercase tracking-widest px-4 py-3 rounded-xl transition-colors flex items-center justify-center gap-2" data-id="element-1017">
                      <ZoomIn className="w-3.5 h-3.5" data-id="element-1018" /> View
                    </button>
                    <button onClick={() => handleSave(cert)} aria-label={`Save ${cert.abbr} certificate`} className="flex-1 sm:flex-none bg-bone hover:bg-ink hover:text-white text-ink font-bold text-[11px] uppercase tracking-widest px-4 py-3 rounded-xl transition-colors flex items-center justify-center gap-2 border border-ink/10" data-id="element-1019">
                      <Download className="w-3.5 h-3.5" data-id="element-1020" /> Save
                    </button>
                    <button onClick={() => handleShare(cert)} aria-label={`Share ${cert.abbr} certificate`} className="flex-1 sm:flex-none bg-bone hover:bg-ink hover:text-white text-ink font-bold text-[11px] uppercase tracking-widest px-4 py-3 rounded-xl transition-colors flex items-center justify-center gap-2 border border-ink/10" data-id="element-1021">
                      {copiedId === cert.id ? <>
                          <Check className="w-3.5 h-3.5 text-green-600" data-id="element-1022" />{' '}
                          Copied
                        </> : <>
                          <Share2 className="w-3.5 h-3.5" data-id="element-1023" /> Share
                        </>}
                    </button>
                  </div>
                </div>
              </motion.article>;
        })}
        </div>

        {/* Trust Section */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="mt-20 md:mt-24 bg-ink rounded-3xl p-8 md:p-16 text-center relative overflow-hidden" data-id="element-1024">
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-gold/10 blur-3xl pointer-events-none" data-id="element-1025" />
          <div className="relative" data-id="element-1026">
            <ShieldCheck className="w-12 h-12 text-gold mx-auto mb-6" data-id="element-1027" />
            <h3 className="text-2xl md:text-3xl font-serif text-white mb-4" data-id="element-1028">
              Trust & Compliance
            </h3>
            <p className="text-white/70 max-w-2xl mx-auto leading-relaxed text-sm md:text-base" data-id="element-1029">
              At Zylus Homes, we believe that trust is the foundation of every
              successful real estate transaction. Our comprehensive licensing
              and certifications ensure that your investments are secure,
              legally sound, and managed by recognized industry professionals.
            </p>
          </div>
        </motion.div>
      </div>

      {/* Lightbox Modal */}
      <AnimatePresence data-id="element-1030">
        {selectedCert && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={() => setSelectedCert(null)} className="fixed inset-0 z-[60] bg-ink/95 backdrop-blur-md flex items-center justify-center p-4 sm:p-6 overflow-y-auto" data-id="element-1031">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 26
        }} onClick={e => e.stopPropagation()} className="relative w-full max-w-4xl my-auto flex flex-col" data-id="element-1032">
              {/* Close */}
              <button onClick={() => setSelectedCert(null)} aria-label="Close" className="absolute -top-2 -right-2 sm:-top-4 sm:-right-4 z-10 w-10 h-10 bg-gold hover:bg-gold-dark text-ink rounded-full flex items-center justify-center transition-colors shadow-xl" data-id="element-1033">
                <X className="w-5 h-5" strokeWidth={2.5} data-id="element-1034" />
              </button>

              {/* Image */}
              <div className="bg-white rounded-2xl p-3 sm:p-6 flex items-center justify-center max-h-[70vh] overflow-hidden" data-id="element-1035">
                <img src={selectedCert.image} alt={selectedCert.title} className="max-w-full max-h-[65vh] w-auto h-auto object-contain rounded-md" data-id="element-1036" />
              </div>

              {/* Info + actions */}
              <div className="mt-4 bg-white/5 backdrop-blur rounded-2xl p-4 sm:p-6 border border-white/10" data-id="element-1037">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4" data-id="element-1038">
                  <div className="min-w-0" data-id="element-1039">
                    <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-gold mb-1.5" data-id="element-1040">
                      {selectedCert.abbr} · {selectedCert.refNo}
                    </div>
                    <h3 className="text-xl sm:text-2xl font-serif text-white mb-2 leading-tight" data-id="element-1041">
                      {selectedCert.title}
                    </h3>
                    <p className="text-white/60 text-sm leading-relaxed" data-id="element-1042">
                      {selectedCert.desc}
                    </p>
                  </div>

                  <div className="flex gap-2 shrink-0" data-id="element-1043">
                    <button onClick={() => handleSave(selectedCert)} className="bg-gold hover:bg-gold-dark text-ink font-bold text-[11px] uppercase tracking-widest px-4 py-2.5 rounded-xl transition-colors flex items-center gap-2" data-id="element-1044">
                      <Download className="w-3.5 h-3.5" data-id="element-1045" /> Save
                    </button>
                    <button onClick={() => handleShare(selectedCert)} className="bg-white/10 hover:bg-white/20 text-white font-bold text-[11px] uppercase tracking-widest px-4 py-2.5 rounded-xl transition-colors flex items-center gap-2 border border-white/15" data-id="element-1046">
                      {copiedId === selectedCert.id ? <>
                          <Check className="w-3.5 h-3.5 text-green-400" data-id="element-1047" />{' '}
                          Copied
                        </> : <>
                          <Share2 className="w-3.5 h-3.5" data-id="element-1048" /> Share
                        </>}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}